import { atom, useRecoilState } from "recoil";

// Word pools used to generate realistic fake company names.
// Larger pools = fewer hash collisions between customers.
const adjectives = [
  "Blue",
  "Silver",
  "Golden",
  "Prime",
  "Global",
  "Apex",
  "Summit",
  "North",
  "River",
  "Oak",
  "Bridge",
  "Atlas",
  "Vertex",
  "Pioneer",
  "Everest",
  "Quantum",
  "Horizon",
  "Aurora",
  "Frontier",
  "Nimbus",
];

const nouns = [
  "Solutions",
  "Systems",
  "Technologies",
  "Networks",
  "Partners",
  "Industries",
  "Holdings",
  "Enterprises",
  "Consulting",
  "Group",
  "Ventures",
  "Dynamics",
  "Works",
  "Labs",
  "Innovations",
  "Strategies",
  "Global",
  "Logistics",
  "Capital",
  "Design",
];

const lastNames = [
  "Smith",
  "Johnson",
  "Brown",
  "Miller",
  "Davis",
  "Wilson",
  "Moore",
  "Taylor",
  "Anderson",
  "Thomas",
  "Jackson",
  "White",
  "Harris",
  "Martin",
  "Thompson",
  "Garcia",
  "Martinez",
  "Robinson",
  "Clark",
  "Rodriguez",
  "Lewis",
  "Lee",
  "Walker",
  "Hall",
  "Allen",
  "Young",
  "King",
  "Wright",
  "Scott",
  "Green",
];

// Deterministic hash of a string — same input always produces the same number.
// Uses a polynomial rolling hash (multiplier 31) truncated to 32-bit unsigned int.
function hashStr(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return hash;
}

// Picks an element from an array deterministically using a numeric seed.
// Bit shifts (>> n) on the same hash produce different but stable offsets,
// ensuring the words chosen within a pattern are independent from each other.
function pick<T>(arr: T[], seed: number): T {
  return arr[Math.abs(seed) % arr.length]!;
}

// Generates a fake company name from a numeric hash.
// 5 patterns give ~63 900 unique combinations in total.
// hash % 5 selects the pattern; bit-shifted variants of hash select the words.
function generateCompanyName(hash: number): string {
  const patterns = [
    () =>
      `${pick(adjectives, hash)} ${pick(nouns, hash >> 3)} ${pick(
        lastNames,
        hash >> 7
      )}`,
    () => `${pick(lastNames, hash)} & ${pick(lastNames, hash >> 4)}`,
    () =>
      `${pick(lastNames, hash)}, ${pick(lastNames, hash >> 5)} and ${pick(
        lastNames,
        hash >> 6
      )}`,
    () =>
      `${pick(lastNames, hash)} ${pick(nouns, hash >> 2)} ${pick(
        adjectives,
        hash >> 7
      )}`,
    () =>
      `${pick(adjectives, hash)} ${pick(lastNames, hash >> 3)} ${pick(
        nouns,
        hash >> 6
      )}`,
  ];
  return patterns[hash % patterns.length]!();
}

export const demoModeState = atom({
  key: "demoModeState",
  default: JSON.parse(localStorage.getItem("demoMode") ?? "false"),
});

/**
 * Manage demoMode localStorage
 */
export function useDemoMode() {
  const [demoMode, setDemoMode] = useRecoilState(demoModeState);

  function toggleDemo() {
    const newValue = !demoMode;
    localStorage.setItem("demoMode", JSON.stringify(newValue));

    const expires = new Date();
    expires.setDate(expires.getDate() + 1);
    document.cookie = `demoMode=${newValue}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

    setDemoMode(newValue);
    window.location.reload();
  }

  return { demoMode, toggleDemo };
}

// Builds a mapping { realName → fakeName } for the entire customer list.
// Customers are sorted alphabetically before processing to ensure a stable
// and deterministic mapping on every reload (same list → same result).
// Each customer's hash is derived from its own name, so most customers always
// get the same fake name even if the list changes.
// The do/while resolves the rare case where two customers produce the same
// generated name: it increments the hash until a unique name is found.
export function anonymizeCustomers(
  customers: string[]
): Record<string, string> {
  const sorted = [...customers].sort();
  const usedNames = new Set<string>();
  const result: Record<string, string> = {};

  sorted.forEach((customer) => {
    let hash = hashStr(customer);
    let name: string;
    do {
      name = generateCompanyName(hash);
      hash++;
    } while (usedNames.has(name));
    usedNames.add(name);
    result[customer] = name;
  });

  return result;
}

// Reverse-lookup: given a fake name and the current mapping, returns the real customer name.
export const getRealCustomer = (
  fakeCustomer: string,
  mapping: Record<string, string>
): string | undefined => {
  return Object.entries(mapping).find(([, fake]) => fake === fakeCustomer)?.[0];
};
